import { Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { Subject } from 'rxjs';

import type {
  CreateNodeView,
  EditorButton,
  EditorMode,
  RtPMPlugin,
} from '@core/prosemirror-legacy';
import { LinkNode } from '@core/prosemirror-legacy';

import { SchedulerLinksServiceInternal } from './scheduler-links-service-internal';
import { SchedulerLinksServicePublic } from './scheduler-links-service-public';
import type { SchedulerAvailabilityInterface, SchedulerLinksDropdownListController } from './types';

export class SchedulerLinksPlugin implements RtPMPlugin {
  public api: { schedulerLinksService: SchedulerLinksServicePublic };
  public buttons = {
    toggleSchedulerLinksList: <EditorButton>{
      icon: 'playlist-add',
      tooltipText: 'wysiwyg.scheduler-links',
      labelTranslationKey: 'app.redactor_inserts.dropdown.heading',
      isActive: () => this.schedulerLinksDropdownListController.isOpen(),
      run: (state, dispatch, origin) => {
        this.schedulerAvailability.isAvailable
          ? this.api.schedulerLinksService.schedulerLinksListOpen(origin)
          : this.schedulerAvailability.openUpsellDialog();
      },
      ...(!this.schedulerAvailability.isAvailable && {
        iconRight: 'upgrade',
        iconRightColor: 'colorIconScaleDefault',
      }),
    },
  };

  private destroy$ = new Subject<void>();
  private editorView: EditorView;
  private schedulerLinksServiceInternal: SchedulerLinksServiceInternal;

  private editorPlugin = new Plugin({
    props: {
      nodeViews: {
        [LinkNode.nodeName]: (node, view, getPos) => {
          const defaultArgs = <const>[node, view, <() => number>getPos];
          return this.linkNodeViewFactory(...defaultArgs);
        },
      },
    },
  });

  constructor(
    private linkNodeViewFactory: CreateNodeView,
    private schedulerLinksDropdownListController: SchedulerLinksDropdownListController,
    private schedulerAvailability: SchedulerAvailabilityInterface,
  ) {}

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
    this.schedulerLinksServiceInternal = new SchedulerLinksServiceInternal(
      editorView,
      this.destroy$,
      this.schedulerLinksDropdownListController,
    );
    this.api = {
      schedulerLinksService: new SchedulerLinksServicePublic(
        editorView,
        this.schedulerLinksServiceInternal,
      ),
    };
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getNativeEditorPlugins() {
    return [this.editorPlugin];
  }
}
